import moment from 'moment-timezone';

export const SCHEDULE_HOURLY = 'hourly';
export const SCHEDULE_DAILY = 'daily';
export const SCHEDULE_WEEKLY = 'weekly';
export const SCHEDULE_MANUALLY = 'manually';
export const SCHEDULE_MONTHLY = 'monthly';

export const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const monthDaysPositions = {
  weekdays: ['first', 'second', 'third', 'fourth', 'fifth', 'last'],
  days: ["first", "last"],
}

export function scheduleToUtc(schedule) {
  if (schedule.type === SCHEDULE_HOURLY) {
    return { type: SCHEDULE_HOURLY, minute: schedule.minute };
  }
  else if (schedule.type === SCHEDULE_DAILY) {
    return { type: SCHEDULE_DAILY, time: schedule.time };
  }
  else if (schedule.type === SCHEDULE_WEEKLY) {
    return { type: SCHEDULE_WEEKLY, time: schedule.time, weekday: schedule.weekday };
  }
  else if (schedule.type === SCHEDULE_MANUALLY) {
    return { type: SCHEDULE_MANUALLY }
  }
  else if (schedule.type === SCHEDULE_MONTHLY) {
    return {
      type: SCHEDULE_MONTHLY,
      month: {
        type: schedule.month.type,
        position: schedule.month.position,
        weekday: schedule.month.weekday,
      },
      time: schedule.time
    }
  }
  return {};
}

export function scheduleToLocal(schedule) {
  if (schedule.type === SCHEDULE_HOURLY) {
    return { type: SCHEDULE_HOURLY, minute: schedule.minute };
  }
  else if (schedule.type === SCHEDULE_DAILY) {
    return { type: SCHEDULE_DAILY, time: schedule.time };
  }
  else if (schedule.type === SCHEDULE_WEEKLY) {
    return { type: SCHEDULE_WEEKLY, time: schedule.time, weekday: schedule.weekday };
  }
  else if (schedule.type === SCHEDULE_MANUALLY) {
    return { type: SCHEDULE_MANUALLY, salesForceObject: schedule.salesForceObject }
  }
  else if (schedule.type === SCHEDULE_MONTHLY) {
    return {
      type: SCHEDULE_MONTHLY,
      month: {
        type: schedule.month.type,
        position: schedule.month.position,
        weekday: schedule.month.weekday,
      },
      time: schedule.time
    }
  }
  return {};
}

export function calculatePannedDate(schedule, timezone, now = moment.utc()) {
  now = moment(now).utc().startOf('minute');
  timezone = timezone || 'UTC';
  const nowInTz = moment(now).tz(timezone);

  if (schedule.type === SCHEDULE_HOURLY) {
    if (!(schedule.minute >= 0 && schedule.minute <= 59)) { throw new Error('Invalid minutes parameter') }
    return now.minutes() >= schedule.minute ? now.add(1, 'hours').minutes(schedule.minute) : now.minutes(schedule.minute);
  }

  const time = moment.tz(schedule.time, 'HH:mm', timezone);

  if (schedule.type === SCHEDULE_DAILY) {
    const calc = nowInTz.clone().hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
    if (calc.isSameOrBefore(nowInTz)) {
      calc.add(1, 'days');
    }
    return calc.utc();
  }
  else if (schedule.type === SCHEDULE_WEEKLY) {
    return schedule.weekday.map(d => {
      const calc = nowInTz.clone().isoWeekday(d).hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
      if (calc.isSameOrBefore(nowInTz)) {
        calc.add(1, 'weeks');
      }
      return calc;
    }).sort((a, b) => a - b)
      .shift()
      .utc();
  }
  else if (schedule.type === SCHEDULE_MONTHLY) {
    let calculateDate;
    if (schedule.month.type === 'weekday') {
      calculateDate = date => calculateMonthWeekdayForPosition(date, schedule.month.position, schedule.month.weekday);
    }
    else if (schedule.month.type === 'day') {
      calculateDate = date => calculateMonthDayForPosition(date, schedule.month.position);
    }
    const calc = calculateDate(nowInTz.clone()).hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
    if (calc.isSameOrBefore(nowInTz)) {
      const nextMonth = calculateDate(nowInTz.clone().add(1, 'month'));
      return nextMonth.hour(time.hour()).minute(time.minute()).second(0).millisecond(0).utc();
    }
    return calc.utc();
  }
  return null;
}

export function checkManualSchedule(schedule) {
  return schedule.type === SCHEDULE_MANUALLY;
}

export function getDocumentName(document_path) {
  try { document_path = decodeURI(document_path || '') }
  catch (error) { console.warn(`Failed to get document name for ${document_path}. Reason: '${error.message}'`) }
  return document_path.split('/').pop();
}

function calculateMonthDayForPosition(date, position) {
  let res = null;
  if (position === 'first') {
    res = date.clone().startOf('month')
  }
  if (position === 'last') {
    res = date.clone().endOf('month')
  }
  return res
}

function calculateMonthWeekdayForPosition(date, position, weekdayName) {
  if (position === 'last') {
    return calculateMonthLastWeekday(date, weekdayName)
  }
  const firstWeekdayOfMonth = date.clone().startOf("month").isoWeekday(weekdayName);
  if (!firstWeekdayOfMonth.isSame(date, 'month')) {
    firstWeekdayOfMonth.add(1, 'week');
  }
  const res = firstWeekdayOfMonth.clone().add(monthDaysPositions.weekdays.indexOf(position), 'week');
  if (!res.isSame(firstWeekdayOfMonth, 'month')) {
    return calculateMonthWeekdayForPosition(date.clone().add(1, 'month'), position, weekdayName)
  }
  return res
}

function calculateMonthLastWeekday(date, weekdayName) {
  const lastWeekdayOfMonth = date.clone().endOf("month").isoWeekday(weekdayName);
  if (!lastWeekdayOfMonth.isSame(date, 'month')) {
    lastWeekdayOfMonth.subtract(1, 'week');
  }
  return lastWeekdayOfMonth
}

/**
 * @deprecated Use {@link calculatePannedDate} instead. This function is kept for migration purposes only.
 */
export function calculatePannedDateLegacy(schedule, now = moment.utc()) {

  const parseTime = (date, time) => {
    const today = moment.utc(time, 'HH:mm');
    if (!today.isValid()) { throw new Error('Invalid time parameter') }

    return today.year(date.year()).month(date.month()).date(date.date());
  }

  now = moment(now).startOf('minute');

  if (schedule.type === SCHEDULE_HOURLY) {
    if (!(schedule.minute >= 0 && schedule.minute <= 59)) { throw new Error('Invalid minutes parameter') }
    return now.minutes() >= schedule.minute ? now.add(1, 'hours').minutes(schedule.minute) : now.minutes(schedule.minute);
  }
  else if (schedule.type === SCHEDULE_DAILY) {
    const calc = parseTime(now, schedule.time);
    if (calc.isSameOrBefore(now)) { calc.add(1, 'days') }
    return calc;
  }
  else if (schedule.type === SCHEDULE_WEEKLY) {
    const calc = parseTime(now, schedule.time);
    return schedule.weekday.map(d => {
      const day = moment(calc).isoWeekday(d);
      return day.isSameOrBefore(now) ? day.add(1, 'weeks') : day;
    }).sort((a, b) => a - b)
      .shift();
  }
  else if (schedule.type === SCHEDULE_MONTHLY) {
    let calculateDate = date => null;
    if (schedule.month.type === 'weekday') {
      calculateDate = date => calculateMonthWeekdayForPosition(date, schedule.month.position, schedule.month.weekday);
    }
    else if (schedule.month.type === 'day') {
      calculateDate = date => calculateMonthDayForPosition(date, schedule.month.position);
    }
    const calc = parseTime(calculateDate(now), schedule.time);
    return calc.isSameOrBefore(now) ? parseTime(calculateDate(now.add(1, 'month')), schedule.time) : calc
  }
  return null;
}